import * as React from 'react'
import { Text, TouchableOpacity, View, StyleSheet } from 'react-native'
import { Button } from '@components/Buttons/Base'
import { Line } from '@components/Line'
import { Spacer } from '@components/Spacer'
import * as Colors from '@themes/Colors'
import Fonts from '@themes/Fonts'
import * as Shadows from '@themes/Shadows'
import { MinusButton } from '../components/MinusButton'
import { PlusButton } from '../components/PlusButton'
import { useEditGoalForm } from '../hooks/useEditGoalForm'
import { useSetGoal } from '../hooks/useGoals'
import type { GoalValues } from '../types'

export function EditGoalScreen({
  notifyDone,
  ...props
}: {
  initial?: Partial<GoalValues>
  notifyDone: () => void
}) {
  const { values, incrementGoal, decrementGoal, togglePeriod } =
    useEditGoalForm(props.initial)

  const saveGoal = useSetGoal()

  return (
    <View style={styles.modalContainer}>
      {!props.initial?.targetValue && (
        <>
          <Text style={styles.innerModalTitle}>Introducing Goals!</Text>
          <Text style={styles.modalText}>
            It’s simple. Choose how many people you’d like to help this year and
            we’ll send you personal monthly updates. We call it living on
            purpose.
          </Text>
          <Line />
        </>
      )}
      <Text style={styles.modalTitle}>My goal is to meet...</Text>
      <View style={styles.controls}>
        <MinusButton
          onPress={() => decrementGoal()}
          disabled={values.target <= 1}
        />
        <Spacer width={35} />
        <Text style={styles.goalNumber}>{values.target}</Text>
        <Spacer width={35} />
        <PlusButton onPress={() => incrementGoal()} />
      </View>
      <Spacer height={14} />
      <View style={[styles.row, styles.tabSelect]}>
        <TouchableOpacity onPress={() => togglePeriod()}>
          <Text style={styles.tabLabel}>Needs per month</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => togglePeriod()}>
          <Text style={styles.tabLabel}>Needs per year</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.tabLabelActive,
            styles[`tabLabelActive_${values.period.adverb}`],
          ]}
        >
          <Text style={styles.activeText}>Needs per {values.period.noun}</Text>
        </TouchableOpacity>
      </View>
      <Spacer height={20} />
      <View
        style={{
          paddingHorizontal: 24,
        }}
      >
        <Button
          title="Set My Goal"
          onClick={() => {
            saveGoal.mutate({
              number: `${values.target}`,
              regularity: values.period.intValue,
            })

            notifyDone()
          }}
          fullWidth
        />
        <Spacer height={8} />
        <Button
          title="Cancel"
          emphasis="secondary"
          onClick={() => notifyDone()}
          fullWidth
        />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  //GOAL Modal
  modalContainer: {
    backgroundColor: Colors.white,
    padding: 22,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  modalTitle: {
    fontFamily: Fonts.type.bold,
    fontSize: Fonts.size.h5,
    marginVertical: 30,
  },
  innerModalTitle: {
    fontFamily: Fonts.type.bold,
    fontSize: Fonts.size.h5,
    textAlign: 'center',
  },
  modalText: {
    fontFamily: Fonts.type.regular,
    fontSize: 14,
    color: Colors.descriptionGray,
    paddingBottom: 4,
    lineHeight: 24,
    textAlign: 'center',
  },
  goalNumber: {
    color: Colors.alternateGray,
    fontFamily: Fonts.type.bold,
    fontWeight: 'bold',
    fontSize: 48,
  },
  row: {
    flexDirection: 'row',
  },
  controls: {
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center',
  },

  //TabSelect
  tabSelect: {
    width: 300,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: Colors.neutralGray,
    marginVertical: 15,
    marginHorizontal: 15,
    paddingHorizontal: 5,
    position: 'relative',
    zIndex: 1,
    height: 44,
  },
  //FROM CARDSTATSTYLE
  tabLabel: {
    fontFamily: Fonts.type.regular,
    fontSize: 15,
    color: Colors.alternateGray,
    opacity: 0.6,
    paddingHorizontal: 15,
    paddingVertical: 10,
    width: 150,
  },

  tabLabelActive: {
    top: -1,
    width: 150,
    paddingHorizontal: 15,
    paddingVertical: 11,
    backgroundColor: Colors.white,
    ...Shadows.defaultShadow,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: Colors.primary,
    position: 'absolute',
    zIndex: 2,
  },

  tabLabelActive_yearly: {
    right: -1,
  },

  tabLabelActive_monthly: {
    left: -1,
  },

  activeText: {
    fontFamily: Fonts.type.alternateBold,
    fontSize: 15,
    textAlign: 'center',
  },

  setGoalButton: {},
  cancelButton: {},
})

export default styles
